import { useMemo } from "react";
import { Briefcase, Clock, CalendarDays } from "lucide-react";
import { format, formatDistanceToNow, differenceInCalendarDays } from "date-fns";
import { ptBR } from "date-fns/locale";

import { TarefaDTO } from "@/types/tarefa.type";

import { Column } from "@/components/table/DataTable";
import { ServerDataTable } from "@/components/table/ServerDataTable";
import { useNavigationHelpers, ROUTES } from "@/lib/navigation-helpers";
import { TarefaStatusBadge } from "@/components/tarefa/TarefaStatusBadge";
import { TarefaFiltrosCriteria, TarefaFiltrosOptions, TarefaStatus } from "@workflow/types";
import { tarefasQueries, tarefasKeys } from "@/services/api/queries/tarefas.queries";
import { TarefaTipoBadge } from "@/components/tarefa/TarefaTipoBadge";
import { PersonBadge } from "@/components/shared/PersonBadge";
import { IdentifierColumnTable } from "@/components/shared/IdentifierColumnTable";

export function TarefaAdminListTable() {
  const { goToTarefaDetails } = useNavigationHelpers();

  const isExecting = (tarefa: TarefaDTO) => {
    const executingStatuses = ["PENDENTE", "EM_EXECUCAO"] as TarefaStatus[];
    return executingStatuses.includes(tarefa.status);
  };

  const tarefaColumns = useMemo<Column<TarefaDTO>[]>(() => {
    const columns: Column<TarefaDTO>[] = [
      {
        key: "tarefa.id",
        title: "ID",
        sortable: true,
        width: "min-w-[60px]",
        render: (tarefa) => <IdentifierColumnTable identifier={tarefa.id} />,
        raw: (tarefa) => tarefa.id.toString(),
      },
      {
        key: "tarefa.nome",
        title: "Tarefa",
        sortable: true,
        width: "min-w-[250px]",
        render: (tarefa) => (
          <div className="flex flex-col py-1">
            <span
              className="truncate font-semibold leading-tight text-foreground"
              title={tarefa.nome}
            >
              {tarefa.nome}
            </span>

            {tarefa.job && (
              <span className="mt-1 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                <Briefcase className="h-3 w-3 text-purple-500/70" /> PIT #{tarefa.job.pit}
              </span>
            )}
          </div>
        ),
        raw: (tarefa) => tarefa.nome,
      },
      {
        key: "tarefa.status",
        title: "Status",
        sortable: true,
        render: (tarefa) => <TarefaStatusBadge status={tarefa.status} />,
        raw: (tarefa) => tarefa.status,
      },
      {
        key: "tarefa.tipo",
        title: "Tipo",
        sortable: true,
        render: (tarefa) => <TarefaTipoBadge tipo={tarefa.tipo} />,
        raw: (tarefa) => tarefa.tipo,
      },
      {
        key: "atribuidoAo.nome",
        title: "Responsável",
        sortable: true,
        render: (tarefa) => <PersonBadge pessoa={tarefa.atribuidoA} isCompact={true} />,
        raw: (tarefa) => tarefa.atribuidoA?.nome || "",
      },
      {
        key: "criadoPor.nome",
        title: "Solicitante",
        sortable: true,
        render: (tarefa) =>
          tarefa.criadoPor ? <PersonBadge pessoa={tarefa.criadoPor} isCompact={true} /> : "-",
        raw: (tarefa) => tarefa.criadoPor?.nome || "",
      },
      {
        key: "tarefa.dataPrazo",
        title: "Prazo",
        sortable: true,
        render: (tarefa) => {
          if (!tarefa.dataPrazo) return <span className="text-sm text-muted-foreground">-</span>;

          const prazo = new Date(tarefa.dataPrazo);
          const diff = differenceInCalendarDays(prazo, new Date());
          const active = isExecting(tarefa);
          const isOverdue = active && diff < 0;
          const isUrgent = active && diff >= 0 && diff <= 2;

          return (
            <div
              className={`flex items-center gap-1.5 text-sm font-medium ${
                isOverdue
                  ? "text-red-500"
                  : isUrgent
                    ? "text-amber-500"
                    : "font-normal text-muted-foreground"
              }`}
            >
              <CalendarDays className="h-3.5 w-3.5 shrink-0 opacity-70" />
              <span>{format(prazo, "dd/MM/yyyy")}</span>
            </div>
          );
        },
        raw: (tarefa) => (tarefa.dataPrazo ? format(new Date(tarefa.dataPrazo), "dd/MM/yyyy") : ""),
      },
      {
        key: "tarefa.criadoEm",
        title: "Criada Em",
        sortable: true,
        render: (tarefa) => (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="h-3.5 w-3.5 opacity-70" />
            {tarefa.criadoEm ? format(new Date(tarefa.criadoEm), "dd/MM/yyyy") : "-"}
          </div>
        ),
      },
      {
        key: "tarefa.alteradoEm",
        title: "Última Atualização",
        sortable: true,
        render: (tarefa) => {
          if (!tarefa.alteradoEm) return <span className="text-sm text-muted-foreground">-</span>;

          const distance = formatDistanceToNow(new Date(tarefa.alteradoEm), {
            addSuffix: true,
            locale: ptBR,
          });

          return (
            <div
              className="flex items-center gap-1.5 text-sm text-muted-foreground"
              title={format(new Date(tarefa.alteradoEm), "dd/MM/yyyy HH:mm")}
            >
              <Clock className="h-3.5 w-3.5 opacity-70" />
              <span className="capitalize-first">{distance}</span>
            </div>
          );
        },
      },
    ];

    return columns;
  }, [goToTarefaDetails]);

  return (
    <ServerDataTable<TarefaDTO, TarefaFiltrosOptions, TarefaFiltrosCriteria>
      config={{
        queryOptions: tarefasQueries.listAdmin,
        filters: tarefasQueries.filtrosAdmin,
        invalidateKey: tarefasKeys.admin,
      }}
      storageKey="tarefas-admin"
      columns={tarefaColumns}
      onRowClick={(tarefa) => goToTarefaDetails(tarefa.id)}
      getRowHref={(tarefa) => ROUTES.tarefaDetails(tarefa.id)}
      emptyMessage="Nenhuma tarefa encontrada."
      errorMessage="Não foi possível carregar as tarefas. Tente recarregar."
      defaultSortColumn="tarefa.id"
      defaultSortOrder="DESC"
      searchPlaceholder="Pesquisar tarefas..."
      rowClassName={(tarefa) => (!isExecting(tarefa) ? "opacity-50" : "")}
    />
  );
}
